import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import BookingProgress from './components/BookingProgress';
import ServiceDetailsForm from './components/ServiceDetailsForm';
import PassengerInfoForm from './components/PassengerInfoForm';
import PaymentForm from './components/PaymentForm';
import BookingSuccess from './components/BookingSuccess';

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);

  const [formData, setFormData] = useState({
    serviceType: '',
    vehicleType: '',
    route: '',
    pickupLocation: '',
    dropLocation: '',
    pickupDate: '',
    pickupTime: '',
    returnDate: '',
    returnTime: '',
    specialRequirements: '',
    fullName: '',
    mobile: '',
    email: '',
    passengers: '',
    pickupAddress: '',
    dropAddress: '',
    paymentMethod: '',
    upiId: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvv: '',
    agreeTerms: false,
    agreeCancellation: false
  });

  const [errors, setErrors] = useState({});
  const [bookingDetails, setBookingDetails] = useState(null);

  const pricingBreakdown = {
    baseFare: 2500,
    distance: 350,
    distanceCharges: 3500,
    nightCharges: 0,
    gst: 300,
    discount: 200,
    total: 6100
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.serviceType) newErrors.serviceType = 'Please select a service type';
      if (!formData?.vehicleType) newErrors.vehicleType = 'Please select a vehicle type';
      if (!formData?.route) newErrors.route = 'Please select a route';
      if (formData?.route === 'custom') {
        if (!formData?.pickupLocation) newErrors.pickupLocation = 'Pickup location is required';
        if (!formData?.dropLocation) newErrors.dropLocation = 'Drop location is required';
      }
      if (!formData?.pickupDate) newErrors.pickupDate = 'Pickup date is required';
      if (!formData?.pickupTime) newErrors.pickupTime = 'Pickup time is required';
      if (formData?.serviceType === 'roundtrip') {
        if (!formData?.returnDate) newErrors.returnDate = 'Return date is required';
        if (!formData?.returnTime) newErrors.returnTime = 'Return time is required';
      }
    }

    if (step === 2) {
      if (!formData?.fullName) newErrors.fullName = 'Full name is required';
      if (!formData?.mobile) {
        newErrors.mobile = 'Mobile number is required';
      } else if (!/^[0-9]{10}$/?.test(formData?.mobile)) {
        newErrors.mobile = 'Please enter a valid 10-digit mobile number';
      }
      if (!formData?.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData?.passengers) {
        newErrors.passengers = 'Number of passengers is required';
      } else if (formData?.passengers < 1 || formData?.passengers > 17) {
        newErrors.passengers = 'Passengers must be between 1 and 17';
      }
      if (!formData?.pickupAddress) newErrors.pickupAddress = 'Pickup address is required';
      if (!formData?.dropAddress) newErrors.dropAddress = 'Drop address is required';
    }

    if (step === 3) {
      if (!formData?.paymentMethod) newErrors.paymentMethod = 'Please select a payment method';
      
      if (formData?.paymentMethod === 'upi' && !formData?.upiId) {
        newErrors.upiId = 'UPI ID is required';
      }
      
      if (formData?.paymentMethod === 'card') {
        if (!formData?.cardNumber) {
          newErrors.cardNumber = 'Card number is required';
        } else if (!/^[0-9]{16}$/?.test(formData?.cardNumber?.replace(/\s/g, ''))) {
          newErrors.cardNumber = 'Please enter a valid 16-digit card number';
        }
        if (!formData?.cardExpiry) {
          newErrors.cardExpiry = 'Expiry date is required';
        } else if (!/^(0[1-9]|1[0-2])\/[0-9]{2}$/?.test(formData?.cardExpiry)) {
          newErrors.cardExpiry = 'Please enter valid expiry (MM/YY)';
        }
        if (!formData?.cardCvv) {
          newErrors.cardCvv = 'CVV is required';
        } else if (!/^[0-9]{3}$/?.test(formData?.cardCvv)) {
          newErrors.cardCvv = 'Please enter valid 3-digit CVV';
        }
      }
      
      if (!formData?.agreeTerms) {
        newErrors.agreeTerms = 'You must agree to terms and conditions';
      }
      if (!formData?.agreeCancellation) {
        newErrors.agreeCancellation = 'You must accept the cancellation policy';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateReferenceNumber = () => {
    const timestamp = Date.now()?.toString(36);
    const random = Math.random()?.toString(36)?.substring(2, 7);
    return `ST${timestamp}${random}`?.toUpperCase();
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsProcessing(true);

    setTimeout(async () => {
      const booking = {
        referenceNumber: generateReferenceNumber(),
        serviceType: formData?.serviceType,
        vehicleType: formData?.vehicleType,
        route: formData?.route === 'custom' 
          ? `${formData?.pickupLocation} → ${formData?.dropLocation}`
          : formData?.route?.split('-')?.map(city => 
              city?.charAt(0)?.toUpperCase() + city?.slice(1)
            )?.join(' → '),
        pickupDate: formData?.pickupDate,
        pickupTime: formData?.pickupTime,
        returnDate: formData?.returnDate,
        returnTime: formData?.returnTime,
        passengers: formData?.passengers,
        fullName: formData?.fullName,
        mobile: formData?.mobile,
        email: formData?.email,
        pickupAddress: formData?.pickupAddress,
        dropAddress: formData?.dropAddress,
        paymentMethod: formData?.paymentMethod,
        totalAmount: pricingBreakdown?.total,
        bookingDate: new Date()?.toISOString(),
        status: 'confirmed'
      };

      // Send email notification to owner
      try {
        const serviceId = import.meta.env?.VITE_EMAILJS_SERVICE_ID;
        const templateId = import.meta.env?.VITE_EMAILJS_TEMPLATE_ID;
        const publicKey = import.meta.env?.VITE_EMAILJS_PUBLIC_KEY;

        if (serviceId && templateId && publicKey) {
          await emailjs?.send(
            serviceId,
            templateId,
            {
              to_email: 'harnamnagpal7@gmail.com',
              booking_reference: booking?.referenceNumber,
              customer_name: booking?.fullName,
              customer_phone: booking?.mobile,
              customer_email: booking?.email,
              service_type: booking?.serviceType,
              vehicle_type: booking?.vehicleType,
              route: booking?.route,
              pickup_date: booking?.pickupDate,
              pickup_time: booking?.pickupTime,
              pickup_address: booking?.pickupAddress,
              drop_address: booking?.dropAddress,
              passengers: booking?.passengers,
              total_amount: `₹${booking?.totalAmount}`,
              payment_method: booking?.paymentMethod,
              booking_date: new Date(booking.bookingDate)?.toLocaleString('en-IN')
            },
            publicKey
          );
          console.log('Email notification sent successfully');
        }
      } catch (error) {
        console.error('Failed to send email notification:', error);
      }

      setBookingDetails(booking);
      setBookingComplete(true);
      setCurrentStep(4);
      setIsProcessing(false);
    }, 2000);
  };

  if (bookingComplete) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-background pt-20 lg:pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <BookingSuccess bookingDetails={bookingDetails} />
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background pt-20 lg:pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 md:mb-8">
            <button
              onClick={() => navigate('/homepage')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
            >
              <span>←</span>
              <span>Back to Home</span>
            </button>
          </div>

          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Complete Your Booking
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Fill in the details below to confirm your journey with Advait Tour and Travels
            </p>
          </div>

          <BookingProgress currentStep={currentStep} />

          {currentStep === 1 && (
            <ServiceDetailsForm
              formData={formData}
              setFormData={setFormData}
              errors={errors}
            />
          )}

          {currentStep === 2 && (
            <PassengerInfoForm
              formData={formData}
              setFormData={setFormData}
              errors={errors}
            />
          )}

          {currentStep === 3 && (
            <PaymentForm
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              pricingBreakdown={pricingBreakdown}
            />
          )}

          <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-center gap-3">
            {currentStep > 1 && (
              <Button
                variant="outline"
                size="lg"
                iconName="ChevronLeft"
                iconPosition="left"
                onClick={handleBack}
                fullWidth
                disabled={isProcessing}
              >
                Previous Step
              </Button>
            )}

            {currentStep < 3 ? (
              <Button
                variant="default"
                size="lg"
                iconName="ChevronRight"
                iconPosition="right"
                onClick={handleNext}
                fullWidth
                className="bg-action hover:bg-action/90"
              >
                Continue to Next Step
              </Button>
            ) : (
              <Button
                variant="default"
                size="lg"
                iconName="CheckCircle"
                iconPosition="left"
                onClick={handleSubmit}
                loading={isProcessing}
                fullWidth
                className="bg-success hover:bg-success/90"
              >
                {isProcessing ? 'Processing Payment...' : 'Confirm & Pay'}
              </Button>
            )}
          </div>

          <div className="mt-6 md:mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Need assistance with booking?
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                variant="outline"
                size="default"
                iconName="Phone"
                iconPosition="left"
                onClick={() => window.open('tel:79986-66660', '_self')}
              >
                79986-66660
              </Button>
              <Button
                variant="default"
                size="default"
                iconName="MessageCircle"
                iconPosition="left"
                onClick={() => window.open('https://wa.me/917998666660', '_blank')}
                className="whatsapp-pulse"
              >
                WhatsApp Support
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default BookingConfirmation;